import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  AccountCircle,
  CreditCard,
  DeleteForever,
  Refresh,
  Email,
  Verified
} from '@mui/icons-material';
import { useAuth } from '../../Context/AuthContext';
import { useBook } from '../../Context/BookContext';
import { useNavigate } from 'react-router-dom';

const AccountPage: React.FC = () => {
  const { user, deleteAccount } = useAuth();
  const { availableImages, isLoadingCredits, fetchImageCredits } = useBook();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');


  // Redirect to credits page for purchases
  const handlePurchaseCredits = () => {
    navigate('/credits');
  };

  const handleDeleteAccount = async () => {
    if (confirmationEmail !== user?.email) return;
    
    try {
      setIsDeleting(true);
      await deleteAccount();
      setDeleteDialogOpen(false);
      setConfirmationEmail('');
    } catch (error) {
      console.error('Error deleting account:', error);
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setConfirmationEmail('');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
        Account Settings
      </Typography>

      <Grid container spacing={3}>
        {/* User Profile Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mb: 2 }}>
                  <AccountCircle sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                  {user?.email || 'User'}
                </Typography>
                {user?.emailVerified && (
                  <Chip
                    icon={<Verified sx={{ fontSize: 16 }} />}
                    label="Verified Account"
                    size="small"
                    sx={{ 
                      backgroundColor: 'success.main',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user?.email}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Credits Management */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CreditCard sx={{ color: 'warning.main', fontSize: 28 }} />
                <Typography variant="h6">
                  Credits Management
                </Typography>
              </Box>
              
              <Box sx={{ p: 2, backgroundColor: 'secondary.light', borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Available Credits
                </Typography>
                {isLoadingCredits ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {availableImages || 0}
                  </Typography>
                )}
              </Box>
              
              <Stack spacing={2}>
                <Button
                  variant="contained"
                  onClick={handlePurchaseCredits}
                  sx={{
                    backgroundColor: 'primary.main',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Buy More Credits
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchImageCredits}
                  sx={{
                    color: 'text.secondary',
                    borderColor: 'divider',
                    textTransform: 'none'
                  }}
                >
                  Refresh Balance
                </Button>
              </Stack>

            </CardContent>
          </Card>
        </Grid>


        {/* Danger Zone */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid', borderColor: 'error.dark' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <DeleteForever sx={{ color: 'error.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ color: 'error.main' }}>
                  Danger Zone
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </Typography>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForever />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  color: 'error.main',
                  borderColor: 'error.dark',
                  '&:hover': {
                    borderColor: 'error.main',
                    backgroundColor: 'error.light',
                    opacity: 0.1,
                  },
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
          }
        }}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to permanently delete your account? This will:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: 'text.secondary' }}>
            <li>Delete your user account and profile</li>
            <li>Remove all your uploaded books</li>
            <li>Delete all generated summaries and subjects</li>
            <li>Remove all your generated images</li>
            <li>Clear your image credits</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Type your email to confirm: <strong>{user?.email}</strong>
          </DialogContentText>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            required
            value={confirmationEmail}
            onChange={(e) => setConfirmationEmail(e.target.value)}
            placeholder={user?.email}
            disabled={isDeleting}
            error={confirmationEmail !== '' && confirmationEmail !== user?.email}
            helperText={confirmationEmail !== '' && confirmationEmail !== user?.email ? 'Email does not match' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting || confirmationEmail !== user?.email}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default AccountPage; 